import {useAtom} from 'jotai';
import styled from 'styled-components';
import {HorizontalScrollArea} from '@/components/HorizentalScrollArea';
import {
  AddressItemType, AddressSearchHistoryItem, AddressSearchItem, SubwaySearchItem
} from '@/map/@types';
import {mobileAddressSearchHistoryAtom} from '@/map/atom';
import {CoordType} from '@/map/enums';
import {Flexbox, IconClose} from '@/styled-components';
import {palette} from '@/styles';
import SearchImage from './search-map-icon.png';

interface IProps {
  onHistoryItemClick(item: AddressSearchHistoryItem, index: number): void;
  onExampleClick(example: ExampleSearchItem): void;
}

interface ExampleSearchItem {
  keyword: string;
  item: AddressItemType;
}

const EXAMPLE_SEARCH: ExampleSearchItem[] = [
  {
    keyword: '홍대입구역',
    item: {
      line: '2호선',
      location: {
        type: CoordType.Point,
        coordinates: [126.9240194, 37.5570156],
      },
      name: '홍대입구역',
      organization: '수도권',
    },
  },
  {
    keyword: '서울 강남구 강남대로92길 19',
    item: {
      addressName: '서울 강남구 역삼동 819-6',
      location: {
        type: CoordType.Point,
        coordinates: [127.02888729145158, 37.49916642978872],
      },
      placeName: '트레바리 강남아지트',
      roadAddressName: '서울 강남구 강남대로92길 19',
    },
  },
  {
    keyword: '덕수궁',
    item: {
      addressName: '서울 중구 정동 5-1',
      location: {
        type: CoordType.Point,
        coordinates: [126.974894754989, 37.5655638710672],
      },
      placeName: '덕수궁',
      roadAddressName: '서울 중구 세종대로 99',
    },
  },
];

const getSearchKeyword = (item: AddressSearchHistoryItem) => {
  if (item.keyword) {
    return item.keyword;
  }

  if (item.origin) {
    if (item.mainKey) {
      return (item.origin as AddressSearchItem)[item.mainKey];
    }

    if ('line' in item.origin) {
      return (item.origin as SubwaySearchItem).name;
    }
  }

  return null;
};

export const SearchHistoryList = ({onHistoryItemClick: onClick, onExampleClick}: IProps) => {
  const [history, setHistory] = useAtom(mobileAddressSearchHistoryAtom);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const dataIdx = e.currentTarget.dataset?.index;
    const index = Number(dataIdx);

    if (index >= 0) {
      onClick?.(history[index], index);
    }
  };

  const handleRemove = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const dataIdx = e.currentTarget.dataset?.index;
    const index = Number(dataIdx);

    if (index >= 0) {
      setHistory((prev) => {
        prev = [...prev];
        prev.splice(index, 1);
        return prev;
      });
    }
  };

  return (
    <Root>
      {!!history?.length && (
        <>
          <Title>최근 검색어</Title>
          <HorizontalScrollArea>
            <List>
              {history.slice(0, 10).map((i, idx) => {
                const keyword = getSearchKeyword(i);

                return !!keyword ? (
                  <Chip key={idx} data-index={idx} onClick={handleClick}>
                    {keyword}
                    <IconClose data-index={idx} onClick={handleRemove} />
                  </Chip>
                ) : null;
              })}
            </List>
          </HorizontalScrollArea>
        </>
      )}
      <Title>
        <img src={SearchImage} width={32} height={32} alt="검색" />
        이렇게 검색해보세요
      </Title>
      <HorizontalScrollArea>
        <List>
          {EXAMPLE_SEARCH.map((i) => (
            <FilledChip key={i.keyword} onClick={() => onExampleClick(i)}>
              {i.keyword}
            </FilledChip>
          ))}
        </List>
      </HorizontalScrollArea>
    </Root>
  );
};

const Root = styled.div``;

const List = styled.div`
  display: flex;
  white-space: nowrap;
  gap: 8px;
  padding: 16px;
`;

const Title = styled(Flexbox)`
  color: ${palette.black};
  font-size: 16px;
  font-weight: 600;
  padding: 20px 16px 0;
`;

const Chip = styled.div`
  display: flex;
  padding: 8px 12px;
  align-items: center;
  gap: 8px;
  border-radius: 999px;
  cursor: pointer;
  border: 1px solid ${palette.gray200};
  color: ${palette.gray700};
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  white-space: nowrap;
`;

const FilledChip = styled(Chip)`
  color: ${palette.primary500};
  background-color: ${palette.primary100};
  border-color: ${palette.primary200};
`;
